import StatusChip from 'components/@extended/StatusChip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import PartyName from 'components/party/PartyName';
import { Button } from 'components/ui/button';
import { ReactNode, useId, useRef, useState } from 'react';
import { useUpdateRelatedPartiesMutation } from 'store/api/client-identifications';
import { ClassifierData } from 'types/classifiers';
import {
    ClientIdentificationPanelDto,
    RelatedPartiesDto,
    RelatedPartyGroupDto,
    RelatedPartyRowDto,
} from 'types/clientIdentification';
import { PartyType } from 'types/contacts';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { allRowUpdates } from '../relatedPartyRows';
import { blockTitleId, MissingChip } from './Block';
import { BLOCK_IDS } from './blockIds';
import { useClientIdentificationPopup } from '../clientIdentificationPopup';
import { successAlert } from 'utils/alert';

/** The role the client's answer on beneficial owners is about; the popup asks that answer above its list. */
export const BENEFICIAL_OWNER_ROLE = 'BENEFICIAL_OWNER';

export function RequiredMark() {
    return <StatusChip type="neutral" size="small" label={t('client-identification.required-mark')} />;
}

export function TextMark() {
    return <StatusChip type="outlined" size="small" label={t('client-identification.text-mark')} />;
}

interface Props {
    contractId: number;
    relatedParties: RelatedPartiesDto;
    panel: ClientIdentificationPanelDto;
    classifiers: Record<string, ClassifierData>;
    canEdit: boolean;
}

/** Sections, order, headings and marks are the backend's: the frontend holds no list of roles. */
export function RelatedPartiesBlock({ contractId, relatedParties, panel, classifiers, canEdit }: Props) {
    const [updateRelatedParties] = useUpdateRelatedPartiesMutation();
    const { openPopup: openModal, closePopup: closeModal } = useClientIdentificationPopup();
    const [removingId, setRemovingId] = useState<number | null>(null);
    const notAskedHeading = useRef<HTMLHeadingElement>(null);
    const ownerAnswer = panel.asksOwnerAnswer ? (
        <div className="text-body1 flex flex-wrap items-center gap-2">
            {panel.ownerAnswer.answer ? (
                t(`client-identification.owner-answer.${panel.ownerAnswer.answer}`)
            ) : (
                <>
                    {t('client-identification.item.OWNER_ANSWER')}
                    {panel.ownerAnswer.isMissing ? <MissingChip /> : null}
                </>
            )}
        </div>
    ) : null;
    const hasOwnerGroup = relatedParties.groups.some((group) => group.roleCode === BENEFICIAL_OWNER_ROLE);

    const notAskedCount = relatedParties.notAskedAtTier.reduce((count, role) => count + role.rows.length, 0);

    const remove = (rowId: number) => {
        // The heading goes with the last row, so focus then moves up to the block's own title.
        const focusAfterRemoval =
            notAskedCount > 1
                ? notAskedHeading.current
                : document.getElementById(blockTitleId(BLOCK_IDS.relatedParties));
        setRemovingId(rowId);
        void updateRelatedParties({
            id: contractId,
            rows: allRowUpdates(relatedParties).filter((row) => row.id !== rowId),
        })
            .unwrap()
            .then(() => {
                successAlert(t('contracts.contract-updated'));
                focusAfterRemoval?.focus();
            })
            .catch(() => undefined)
            .finally(() => setRemovingId(null));
    };

    const confirmRemove = (row: RelatedPartyRowDto, roleName: string) => {
        const name = row.linkedPartyName ?? row.name ?? '';
        openModal(
            <ConfirmationModal
                titleId="client-identification.remove-party-title"
                titleValues={{ name, role: roleName }}
                confirmId="remove"
                content={
                    <p className="text-body1 m-0">
                        {t('client-identification.remove-party-body', {
                            name,
                            role: roleName,
                            contact: row.linkedPartyId === null ? 'no' : 'yes',
                        })}
                    </p>
                }
                onCancel={closeModal}
                onConfirm={() => {
                    closeModal();
                    remove(row.id);
                }}
            />
        );
    };

    return (
        <div className="flex flex-col gap-4">
            {hasOwnerGroup ? null : ownerAnswer}
            {relatedParties.groups.map((group) => (
                <GroupSection key={group.roleCode} group={group} classifiers={classifiers}>
                    {group.roleCode === BENEFICIAL_OWNER_ROLE ? ownerAnswer : null}
                </GroupSection>
            ))}
            {relatedParties.notAskedAtTier.some((role) => role.rows.length) ? (
                <section className="flex flex-col gap-2">
                    <h4 ref={notAskedHeading} tabIndex={-1} className="text-body1 m-0 font-semibold outline-none">
                        {t('client-identification.not-asked-at-tier')}
                    </h4>
                    {relatedParties.notAskedAtTier.flatMap((role) =>
                        role.rows.map((row) => (
                            <div key={row.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <PartyRow
                                    row={row}
                                    classifiers={classifiers}
                                    roleName={role.name}
                                    className="flex-[1_1_12rem]"
                                />
                                {canEdit ? (
                                    <RemoveButton
                                        name={row.linkedPartyName ?? row.name ?? ''}
                                        disabled={removingId === row.id}
                                        onRemove={() => confirmRemove(row, role.name)}
                                    />
                                ) : null}
                            </div>
                        ))
                    )}
                </section>
            ) : null}
        </div>
    );
}

function GroupSection({
    group,
    classifiers,
    children,
}: {
    group: RelatedPartyGroupDto;
    classifiers: Record<string, ClassifierData>;
    children?: ReactNode;
}) {
    const headingId = useId();
    return (
        <section aria-labelledby={headingId} className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
                <h4 id={headingId} className="text-body1 m-0 font-semibold">
                    {group.namePlural}
                </h4>
                {group.isMandatory ? <RequiredMark /> : null}
            </div>
            {children}
            {group.rows.length ? (
                group.rows.map((row) => (
                    <div key={row.id} className="flex flex-col gap-0.5">
                        <PartyRow row={row} classifiers={classifiers} />
                        {!row.linkedPartyId && group.isContactsOnly ? (
                            <span className="text-caption text-foreground-secondary">
                                {t('client-identification.contacts-only-hint')}
                            </span>
                        ) : null}
                    </div>
                ))
            ) : (
                <div className="text-body1 text-foreground-secondary flex items-center gap-2">
                    {t('client-identification.nobody-listed')}
                    {group.isMandatory ? <MissingChip /> : null}
                </div>
            )}
        </section>
    );
}

function PartyRow({
    row,
    classifiers,
    roleName,
    className,
}: {
    row: RelatedPartyRowDto;
    classifiers: Record<string, ClassifierData>;
    roleName?: string;
    className?: string;
}) {
    const typeWord = row.linkedPartyType ? t(row.linkedPartyType === PartyType.COMPANY ? 'company' : 'person') : null;
    const actingRole = row.actingRoleCode
        ? getClassifierText(classifiers['ACTING_FOR_CLIENT_ROLE'], row.actingRoleCode) || row.actingRoleCode
        : null;
    const details = [roleName, typeWord, actingRole, row.note].filter(Boolean).join(' · ');
    return (
        <div className={cn('flex min-w-0 flex-col', className)}>
            <RowName row={row} isLink />
            {details ? <span className="text-caption text-foreground-secondary">{details}</span> : null}
        </div>
    );
}

/** A row's person: the linked contact, or the typed name with its mark. */
export function RowName({
    row,
    isLink,
}: {
    row: Pick<RelatedPartyRowDto, 'linkedPartyId' | 'linkedPartyName' | 'linkedPartyType' | 'name'>;
    isLink?: boolean;
}) {
    if (row.linkedPartyId !== null && row.linkedPartyType) {
        return (
            <PartyName
                party={{ id: row.linkedPartyId, name: row.linkedPartyName ?? '', partyType: row.linkedPartyType }}
                isLink={isLink}
                renderPartyIcon
                variant="readonly-other"
                // A long name wraps beside its icon, which stays level with the first line.
                className="flex-nowrap items-start [&>:first-child]:h-lh [&>:first-child]:items-center"
            />
        );
    }
    return (
        <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{row.name}</span>
            <TextMark />
        </span>
    );
}

export function RemoveButton({ name, disabled, onRemove }: { name: string; disabled?: boolean; onRemove: () => void }) {
    return (
        <Button
            {...buttonStyle({ variant: 'text', size: 'small' })}
            disabled={disabled}
            focusableWhenDisabled
            onClick={onRemove}
            aria-label={t('client-identification.remove-named', { name })}
        >
            {t('remove')}
        </Button>
    );
}
